import { Navigate, useLocation } from 'react-router-dom';
import { AppShellSkeleton } from '@/components/core';
import { useProfileCompletion } from '@/modules/hr-directory';
import { useAuthStore } from '@/state/store/auth';

const PROFILE_COMPLETION_PATH = '/profile/edit';

/** Pages a person who still has to complete their profile may open: their profile (password, MFA, devices) and its editor. */
const PATHS_OPEN_DURING_COMPLETION = ['/profile', PROFILE_COMPLETION_PATH];

/**
 * Legacy `ProfileCompletionGuard`: right after login, a person who must provide extended profile
 * information (`hrm::profile::require-extended-info`) and has not saved their own profile yet is sent
 * to "Edit profile" from every other page until they do. Everyone else goes straight to the app.
 */
export const ProfileCompletionGate = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const { isLoading, isFetching, isCompletionRequired } = useProfileCompletion();

  // Like `ProtectedRoute`: right after sign-in the account and its permissions are still loading, and
  // deciding now would let the requested page render (and load its data) before the redirect.
  if (isAuthenticated && !user) return <AppShellSkeleton />;
  if (isLoading) return <AppShellSkeleton />;

  // While the profile is refetched after a save, the cached copy still says "not completed".
  if (isCompletionRequired && !isFetching && !PATHS_OPEN_DURING_COMPLETION.includes(pathname)) {
    return <Navigate to={PROFILE_COMPLETION_PATH} replace />;
  }

  return <>{children}</>;
};
